"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkUpdateReportAck = exports.LinkUpdateReport = exports.LinkUpdateDownloadGrant = exports.LinkUpdatesResponse = exports.LinkUpdateAssignment = exports.LinkInstallBlock = exports.LinkRuntimeStateAck = exports.LinkRuntimeStateReport = exports.LinkBusyState = exports.LinkArtifactManifest = exports.LinkPackageRefusal = exports.LINK_DEBIAN_PACKAGE_NAME = exports.LINK_PACKAGE_FORMAT_FOR_PLATFORM = exports.LinkPackageFormat = exports.LinkPackageArchitecture = exports.LinkPackagePlatform = exports.LINK_UPDATES_SCHEMA_VERSION = void 0;
exports.isMsiCompatibleLinkVersion = isMsiCompatibleLinkVersion;
exports.compareLinkVersions = compareLinkVersions;
exports.linkManifestRefusal = linkManifestRefusal;
exports.linkPackageRefusal = linkPackageRefusal;
const zod_1 = require("zod");
const provision_js_1 = require("./provision.js");
/**
 * Back Office → re:form.lite.link update channel (RL-454, ADR-0014 §7).
 *
 * Link is distributed and updated through Terminal Management like the till
 * packages, never by direct download: a signed Debian package on Linux, a
 * signed MSI installer for the Windows service. This file is the contract the
 * Link-side updater (RL-456 / RL-461) calls; Back Office owns it.
 *
 * ------------------------------------------------------------------
 * THE ROUND TRIP
 * ------------------------------------------------------------------
 *   1. `POST /api/v1/link/updates/state`   — what the Link runs, and whether it
 *      is busy (an export in flight, an import run active). Sent on every
 *      poll, and again after an install restarts the service.
 *   2. `GET  /api/v1/link/updates`         — what is assigned to this Link: at
 *      most one update, the newest rollout that reaches its store, carrying
 *      the manifest the package must match.
 *   3. `GET  /api/v1/link/updates/:rolloutTargetId/artifact` — a ten-minute
 *      presigned URL for the bytes. Fetch it WITHOUT the Link's bearer token:
 *      it points at object storage, not at Back Office.
 *   4. Verify: byte count, SHA-256, then the Ed25519 signature (below).
 *   5. `POST /api/v1/link/updates/:rolloutTargetId/report` — progress, in the
 *      Provision update-state vocabulary tills use (`ProvisionUpdateState`).
 *
 * All routes take the Link's own terminal token (kind `link`); a till token is
 * refused with 403. The till update routes (`/provision/rollout-work` …)
 * refuse a Link token the same way, so neither kind can receive the other's
 * package.
 *
 * ------------------------------------------------------------------
 * SIGNATURE
 * ------------------------------------------------------------------
 * Exactly the terminal artifact scheme (`tools/provision/publish_artifact.py`
 * in the terminals repository): `signature` is a base64 Ed25519 signature over
 * the raw 32-byte SHA-256 digest of the package bytes, made by the release
 * signer with key `signingKeyId`. Back Office never holds the private key and
 * does not vouch for the bytes; it only carries the signature. The Link holds
 * the public keys it trusts (built into the package), refuses an unknown
 * `signingKeyId`, and fails closed on an empty trust store.
 *
 * ------------------------------------------------------------------
 * WHAT THE SIGNATURE DOES NOT COVER — AND THE CHECKS THAT DO
 * ------------------------------------------------------------------
 * The signature covers the bytes and nothing else. The manifest fields beside
 * it (version, platform, architecture, format) are Back Office's word, and a
 * compromised or misconfigured Back Office — or anyone who can answer for it —
 * could pair a genuinely signed package with a manifest that lies about it:
 * an OLDER Link package relabelled as newer (a downgrade to a release with a
 * known hole), a package for the other platform, or a till package, which the
 * same release signer signs. Every one of those passes the signature check.
 *
 * So after the signature verifies, and before anything is installed, the Link
 * MUST read the package's own identity from inside the verified bytes — which
 * the signature does cover — and refuse unless `linkPackageRefusal` returns
 * null for it:
 *
 *   - `.deb`: the control fields `Package`, `Version`, `Architecture`
 *     (`dpkg-deb --field <file> Package Version Architecture`); `Package` must
 *     be `reform-lite-link`, `Architecture` `amd64` → `x64`, `arm64` → `arm64`.
 *   - `.msi`: the Property table's `UpgradeCode` (the Link's own, fixed for
 *     the product) and `ProductVersion`, and the Summary Information
 *     `Template` platform (`x64` / `Arm64`).
 *
 * The rules (`linkPackageRefusal`), each reported as the `errorCode` of a
 * `failed` report:
 *   1. the manifest is for this Link's own platform, architecture and format —
 *      else `LINK_PACKAGE_PLATFORM_MISMATCH`, checked BEFORE downloading;
 *   2. the package's embedded identity is the Link's and says exactly what the
 *      manifest says — else `LINK_PACKAGE_IDENTITY_MISMATCH`;
 *   3. its version is strictly newer than the running one, by SemVer
 *      precedence (`compareLinkVersions`) — else `LINK_UPDATE_DOWNGRADE_REFUSED`.
 *      There is no rollback through this channel: returning to older code
 *      means releasing it again under a newer version.
 *
 * Back Office refuses to assign an older version too (the target fails with
 * `LINK_UPDATE_DOWNGRADE_REFUSED`), but that only catches its own mistakes; the
 * Link-side check is the one that holds against Back Office. Back Office treats
 * both refusal codes as security events (a critical Provision notification).
 *
 * A Windows package's version is plain `major.minor.patch` inside the MSI
 * ProductVersion limits (255.255.65535): the MSI cannot carry a pre-release
 * tag, so a tagged version could not be matched against it (rule 2) nor
 * ordered (rule 3). Publication refuses anything else for `windows`.
 *
 * ------------------------------------------------------------------
 * THE BUSY RULE
 * ------------------------------------------------------------------
 * The defer-while-busy rule tills follow (`waiting_for_idle`): a Link does not
 * install while an export is in flight or an import run is active. Back Office
 * enforces it on its side as well — an `installing` report is refused (409)
 * unless the Link's latest state report is fresh and says it is idle — so a
 * Link that restarts mid-export does not lose a receipt because its own check
 * raced. A busy Link reports `waiting_for_idle` and tries again.
 */
exports.LINK_UPDATES_SCHEMA_VERSION = 1;
exports.LinkPackagePlatform = zod_1.z.enum(['linux', 'windows']);
exports.LinkPackageArchitecture = zod_1.z.enum(['x64', 'arm64']);
/** `deb` with a systemd unit on Linux, a signed `msi` for the Windows service. */
exports.LinkPackageFormat = zod_1.z.enum(['deb', 'msi']);
/** The one package format each platform ships as. */
exports.LINK_PACKAGE_FORMAT_FOR_PLATFORM = { linux: 'deb', windows: 'msi' };
const SemanticVersion = zod_1.z
    .string()
    .trim()
    .regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/, 'Invalid semantic version')
    .max(80);
const Sha256 = zod_1.z.string().regex(/^[a-f0-9]{64}$/);
/** The Debian package name every Link `.deb` carries in its control file. */
exports.LINK_DEBIAN_PACKAGE_NAME = 'reform-lite-link';
/** MSI ProductVersion field limits: major.minor.build. */
const MSI_VERSION_LIMITS = [255, 255, 65535];
/**
 * True when `version` can be a Windows MSI ProductVersion exactly: plain
 * `major.minor.patch`, no pre-release tag, each part inside the MSI limits.
 */
function isMsiCompatibleLinkVersion(version) {
    const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(version);
    if (match === null)
        return false;
    return MSI_VERSION_LIMITS.every((limit, index) => Number(match[index + 1]) <= limit);
}
/**
 * SemVer 2.0 precedence of two Link versions (`x.y.z` with an optional
 * pre-release tag): negative when `left` is older, 0 when equal, positive when
 * newer. `1.4.0-rc.1` < `1.4.0`, `1.4.0-rc.2` < `1.4.0-rc.10`. Throws on a
 * string that is not a Link version, so a malformed one is never ordered.
 */
function compareLinkVersions(left, right) {
    const parse = (value) => {
        const match = /^(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?$/.exec(value.trim());
        if (match === null)
            throw new Error(`Not a Link version: ${value}`);
        return {
            core: [match[1], match[2], match[3]].map((part) => BigInt(part)),
            pre: match[4]?.split('.') ?? [],
        };
    };
    const a = parse(left);
    const b = parse(right);
    for (let index = 0; index < 3; index += 1) {
        if (a.core[index] !== b.core[index])
            return a.core[index] < b.core[index] ? -1 : 1;
    }
    if (a.pre.length === 0 || b.pre.length === 0)
        return b.pre.length - a.pre.length;
    for (let index = 0; index < Math.min(a.pre.length, b.pre.length); index += 1) {
        const x = a.pre[index];
        const y = b.pre[index];
        if (x === y)
            continue;
        const xNumeric = /^\d+$/.test(x);
        const yNumeric = /^\d+$/.test(y);
        if (xNumeric && yNumeric)
            return BigInt(x) < BigInt(y) ? -1 : 1;
        if (xNumeric !== yNumeric)
            return xNumeric ? -1 : 1;
        return x < y ? -1 : 1;
    }
    return a.pre.length - b.pre.length;
}
/**
 * Why a Link refuses a package the signature accepted (see WHAT THE SIGNATURE
 * DOES NOT COVER). Reported as the `errorCode` of a `failed` report.
 */
exports.LinkPackageRefusal = zod_1.z.enum([
    'LINK_PACKAGE_PLATFORM_MISMATCH',
    'LINK_PACKAGE_IDENTITY_MISMATCH',
    'LINK_UPDATE_DOWNGRADE_REFUSED',
]);
/**
 * What the Link verifies before it installs. Everything a package must match
 * is here; the presigned URL carries nothing the Link should trust.
 */
exports.LinkArtifactManifest = zod_1.z
    .object({
    artifactId: zod_1.z.string().min(1),
    applicationType: zod_1.z.literal('link'),
    semanticVersion: SemanticVersion,
    platform: exports.LinkPackagePlatform,
    architecture: exports.LinkPackageArchitecture,
    packageFormat: exports.LinkPackageFormat,
    /** Decimal string: a package can outgrow a JSON-safe integer in principle. */
    sizeBytes: zod_1.z.string().regex(/^\d+$/),
    sha256: Sha256,
    /** Base64 Ed25519 over the raw SHA-256 digest — see SIGNATURE above. */
    signature: zod_1.z.string().min(1),
    signingKeyId: zod_1.z.string().min(1),
    signatureScheme: zod_1.z.literal('ed25519-sha256-digest'),
    releaseNotes: zod_1.z.string().nullable(),
})
    .strict();
/**
 * The manifest-only part of the check (rules 1 and 3), made before the Link
 * downloads anything. `linkPackageRefusal` repeats it with the package's own
 * identity once the bytes have verified.
 */
function linkManifestRefusal(manifest, self) {
    if (manifest.platform !== self.platform ||
        manifest.architecture !== self.architecture ||
        manifest.packageFormat !== exports.LINK_PACKAGE_FORMAT_FOR_PLATFORM[self.platform]) {
        return 'LINK_PACKAGE_PLATFORM_MISMATCH';
    }
    if (compareLinkVersions(manifest.semanticVersion, self.semanticVersion) <= 0) {
        return 'LINK_UPDATE_DOWNGRADE_REFUSED';
    }
    return null;
}
/**
 * The full pre-install check, after size, SHA-256 and signature verified.
 * Null means install. The version compared against the running one is the
 * package's EMBEDDED version, which the signature covers — never only the
 * manifest's, which it does not.
 */
function linkPackageRefusal(manifest, pkg, self) {
    const manifestRefusal = linkManifestRefusal(manifest, self);
    if (manifestRefusal === 'LINK_PACKAGE_PLATFORM_MISMATCH')
        return manifestRefusal;
    if (pkg.format !== manifest.packageFormat ||
        pkg.packageName !== self.packageName ||
        pkg.version !== manifest.semanticVersion ||
        pkg.architecture !== manifest.architecture) {
        return 'LINK_PACKAGE_IDENTITY_MISMATCH';
    }
    let order;
    try {
        order = compareLinkVersions(pkg.version, self.semanticVersion);
    }
    catch {
        return 'LINK_PACKAGE_IDENTITY_MISMATCH';
    }
    return order > 0 ? null : 'LINK_UPDATE_DOWNGRADE_REFUSED';
}
/** Link's own work that an install must not interrupt. */
exports.LinkBusyState = zod_1.z
    .object({
    /** A receipt or shift event is being written to the store system. */
    exportInFlight: zod_1.z.boolean(),
    /** An import run (catalogue, cashiers …) is active. */
    importRunActive: zod_1.z.boolean(),
})
    .strict();
/** `POST /api/v1/link/updates/state` — what the Link runs, and whether it is busy. */
exports.LinkRuntimeStateReport = zod_1.z
    .object({
    semanticVersion: SemanticVersion,
    platform: exports.LinkPackagePlatform,
    architecture: exports.LinkPackageArchitecture,
    /**
     * The Link artifact it was installed from, when it was installed through
     * this channel; null for a first, hand-installed package.
     */
    artifactId: zod_1.z.string().min(1).max(160).nullable(),
    sha256: Sha256.nullable(),
    busy: exports.LinkBusyState,
    reportedAt: zod_1.z.string().datetime(),
})
    .strict();
exports.LinkRuntimeStateAck = zod_1.z
    .object({
    terminalId: zod_1.z.string(),
    semanticVersion: zod_1.z.string(),
    busy: zod_1.z.boolean(),
    receivedAt: zod_1.z.string().datetime(),
})
    .strict();
/**
 * Why an assigned package may not be installed yet. `null` on the assignment
 * means it may.
 */
exports.LinkInstallBlock = zod_1.z.enum([
    /** No integrity-verified `downloaded` report for this attempt yet. */
    'download_unverified',
    /** The latest state report says an export or an import run is active. */
    'busy',
    /** No state report recent enough to know whether the Link is idle. */
    'runtime_state_stale',
]);
exports.LinkUpdateAssignment = zod_1.z
    .object({
    rolloutTargetId: zod_1.z.string().min(1),
    rolloutId: zod_1.z.string().min(1),
    /** Reports must echo it; a report for an older attempt is refused. */
    attempt: zod_1.z.number().int().positive(),
    state: provision_js_1.ProvisionUpdateState,
    artifact: exports.LinkArtifactManifest,
    installAuthorized: zod_1.z.boolean(),
    installBlockedBy: exports.LinkInstallBlock.nullable(),
})
    .strict();
/** `GET /api/v1/link/updates`. */
exports.LinkUpdatesResponse = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(exports.LINK_UPDATES_SCHEMA_VERSION),
    /**
     * The last state this Link reported, or null before its first report. A
     * Link with no reported state is assigned nothing: Back Office cannot pick
     * a package for a platform it has not been told.
     */
    runtime: zod_1.z
        .object({
        semanticVersion: zod_1.z.string(),
        platform: exports.LinkPackagePlatform,
        architecture: exports.LinkPackageArchitecture,
        busy: zod_1.z.boolean(),
        reportedAt: zod_1.z.string().datetime(),
    })
        .strict()
        .nullable(),
    /** At most one: the newest rollout that reaches this Link's store. */
    assignments: zod_1.z.array(exports.LinkUpdateAssignment).max(1),
})
    .strict();
/** `GET /api/v1/link/updates/:rolloutTargetId/artifact`. */
exports.LinkUpdateDownloadGrant = zod_1.z
    .object({
    rolloutTargetId: zod_1.z.string().min(1),
    artifact: exports.LinkArtifactManifest,
    /** Presigned object-storage URL. Never send the terminal token to it. */
    url: zod_1.z.string().url(),
    expiresAt: zod_1.z.string().datetime(),
})
    .strict();
/** `POST /api/v1/link/updates/:rolloutTargetId/report`. */
exports.LinkUpdateReport = zod_1.z
    .object({
    /** Idempotency key: a retried report with the same id is acknowledged, not re-applied. */
    clientEventId: zod_1.z.string().uuid(),
    attempt: zod_1.z.number().int().positive(),
    state: provision_js_1.ProvisionUpdateState,
    /** Required with `downloaded`: size, SHA-256 and signature all checked. */
    integrityVerified: zod_1.z.boolean().optional(),
    errorCode: zod_1.z
        .string()
        .regex(/^[A-Z0-9_.-]+$/)
        .max(120)
        .nullable()
        .optional(),
    reportedAt: zod_1.z.string().datetime(),
})
    .strict()
    .superRefine((report, context) => {
    if (report.state === 'downloaded' && report.integrityVerified === undefined) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['integrityVerified'],
            message: 'A downloaded report must say whether the package verified',
        });
    }
});
exports.LinkUpdateReportAck = zod_1.z
    .object({
    reportId: zod_1.z.string(),
    rolloutTargetId: zod_1.z.string(),
    state: provision_js_1.ProvisionUpdateState,
    duplicate: zod_1.z.boolean(),
    receivedAt: zod_1.z.string().datetime(),
})
    .strict();
//# sourceMappingURL=link-update.js.map